import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Instagram, Loader2, Send, Save, Trash2, CheckCircle2, Clock, AlertTriangle } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DiscountInstagram } from "@/components/DiscountInstagram";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS = {
  draft: { label: "Borrador", cls: "bg-muted text-muted-foreground", icon: Clock },
  approved: { label: "Aprobado · en cola", cls: "bg-sky-100 text-sky-800", icon: Clock },
  published: { label: "Publicado", cls: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  failed: { label: "Error al publicar", cls: "bg-red-100 text-red-800", icon: AlertTriangle },
};

function StatusChip({ s, id }) {
  const st = STATUS[s];
  if (!st) return <span className="text-xs text-muted-foreground" data-testid={`ig-status-${id}`}>Sin liberar</span>;
  return <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 ${st.cls}`} data-testid={`ig-status-${id}`}><st.icon className="w-3 h-3" />{st.label}</span>;
}

function PostEditor({ wid, configured, onClose, onChanged }) {
  const [post, setPost] = useState(null);
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.post(`/admin/instagram/${wid}/draft`).then((r) => { setPost(r.data); setCaption(r.data.caption); onChanged(); })
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); onClose(); });
  }, [wid]); // eslint-disable-line react-hooks/exhaustive-deps
  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const save = () => api.put(`/admin/instagram/${wid}`, { caption });
  const approve = async () => {
    await save();
    const { data } = await api.post(`/admin/instagram/${wid}/approve`);
    if (data.status === "failed") throw { response: { data: { detail: `Instagram respondió: ${data.error}` } } };
  };
  if (!post) return <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const tags = (caption.match(/#/g) || []).length;
  return (
    <div className="space-y-4" data-testid="ig-editor">
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="ig-media-strip">
        {post.media.map((u, i) => (
          <img key={u} src={u.replace(/^https?:\/\/[^/]+/, BACKEND)} alt={i === 0 ? "Portada" : `Foto ${i}`}
            className="w-28 h-28 rounded-lg object-cover shrink-0 border" data-testid={`ig-media-${i}`} />
        ))}
      </div>
      <Textarea rows={12} value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={2200} className="text-sm" data-testid="ig-caption-input" />
      <p className={`text-xs text-right ${tags > 30 ? "text-destructive" : "text-muted-foreground"}`} data-testid="ig-caption-counter">{caption.length}/2200 caracteres · {tags}/30 hashtags</p>
      {!configured && <p className="text-xs rounded-lg bg-amber-50 border border-amber-300 text-amber-800 p-2" data-testid="ig-queue-note">La cuenta de Instagram aún no está conectada. Al aprobar, la publicación queda en cola y se publicará cuando se conecte Meta.</p>}
      <div className="flex flex-wrap justify-end gap-2">
        <Button variant="ghost" size="sm" className="text-destructive" disabled={busy} onClick={() => run(() => api.delete(`/admin/instagram/${wid}`), "Publicación quitada")} data-testid="ig-remove-btn"><Trash2 className="w-4 h-4 mr-1" />Quitar</Button>
        <Button variant="outline" size="sm" disabled={busy} onClick={() => run(save, "Borrador guardado")} data-testid="ig-save-btn"><Save className="w-4 h-4 mr-1" />Guardar</Button>
        <Button size="sm" disabled={busy || tags > 30} onClick={() => run(approve, configured ? "Publicado en Instagram" : "Aprobado: quedó en cola")} data-testid="ig-approve-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" />{configured ? "Aprobar y publicar" : "Aprobar"}</>}
        </Button>
      </div>
    </div>
  );
}

function WorkshopRow({ w, onOpen }) {
  const s = w.instagram?.status;
  return (
    <div className="flex items-center gap-3 rounded-xl border p-3" data-testid={`ig-row-${w.id}`}>
      <div className="min-w-0 flex-1">
        <p className="font-semibold truncate">{w.name}</p>
        <p className="text-xs text-muted-foreground truncate">{w.business_name}{w.commune ? ` · ${w.commune}` : ""} · {w.photos} foto(s)</p>
        <div className="mt-1"><StatusChip s={s} id={w.id} /></div>
      </div>
      {s !== "published" && <Button size="sm" variant={s ? "outline" : "default"} onClick={onOpen} data-testid={`ig-open-${w.id}`}>{s ? "Revisar" : "Liberar"}</Button>}
    </div>
  );
}

export function InstagramManager() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/instagram").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  return (
    <Card className="p-6 space-y-4 mt-6" data-testid="instagram-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2"><Instagram className="w-5 h-5 text-primary" /><h3 className="font-bold">Instagram @vivotaller.cl</h3></div>
        {data && <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 ${data.configured ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid="ig-connection-status">{data.configured ? "Conectado" : "Pendiente de conectar Meta"}</span>}
      </div>
      <p className="text-sm text-muted-foreground">Libera los talleres que quieres mostrar en Instagram. Cada publicación es un carrusel con una portada de VIVOTaller y hasta 4 fotos del taller, con la comuna y hashtags de la zona para llegar a personas de ese lugar.</p>
      {!data ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : data.workshops.length === 0
        ? <p className="text-sm text-muted-foreground" data-testid="ig-empty">No hay talleres publicados todavía.</p>
        : <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">{data.workshops.map((w) => <WorkshopRow key={w.id} w={w} onOpen={() => setOpen(w)} />)}</div>}
      <Dialog open={!!open} onOpenChange={(v) => { if (!v) setOpen(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="ig-dialog">
          <DialogHeader>
            <DialogTitle>Publicación para Instagram</DialogTitle>
            <DialogDescription>{open?.name}</DialogDescription>
          </DialogHeader>
          {open && <PostEditor wid={open.id} configured={data?.configured} onClose={() => setOpen(null)} onChanged={load} />}
        </DialogContent>
      </Dialog>
      {data && <DiscountInstagram configured={data.configured} StatusChip={StatusChip} />}
    </Card>
  );
}
